/**
 * Background Helper Functions
 *
 * Utility functions for generating backgrounds and card backgrounds with support for:
 * - Gradients with angle control
 * - Gradient favorability (color balance)
 * - Gradient strength (color blending)
 * - Opacity control
 * - Solid color fallbacks
 */

type ThemeColors = {
  primaryColor: string;
  secondaryColor: string;
  cardBg: string;
  cardGradientColor1?: string;
  cardGradientColor2?: string;
};

type CustomizationSettings = {
  gradientEnabled?: boolean;
  gradientAngle?: number;
  gradientFavorability?: number;
  gradientStrength?: number;
  cardGradientEnabled?: boolean;
  cardGradientAngle?: number;
  cardGradientFavorability?: number;
  cardGradientStrength?: number;
  cardOpacity?: number;
};

/**
 * Helper to convert hex color to rgba
 */
export const hexToRgba = (hex: string, alpha: number, fallback?: string): string => {
  // Validate input
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || hex.length < 7) {
    if (fallback) {
      console.warn(`Invalid hex color: ${hex}, using provided fallback`);
      return hexToRgba(fallback, alpha);
    }
    console.error(`Invalid hex color: ${hex}, and no fallback provided`);
    return `rgba(0, 0, 0, ${alpha})`; // Last resort: black
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Check for NaN values
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    if (fallback) {
      console.warn(`Failed to parse hex color: ${hex}, using provided fallback`);
      return hexToRgba(fallback, alpha);
    }
    console.error(`Failed to parse hex color: ${hex}, and no fallback provided`);
    return `rgba(0, 0, 0, ${alpha})`; // Last resort: black
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Helper to blend two hex colors together
 * @param color1 First color (hex)
 * @param color2 Second color (hex)
 * @param ratio Blend ratio (0 = all color1, 1 = all color2)
 * @returns Blended color (hex)
 */
export const blendColors = (color1: string, color2: string, ratio: number): string => {
  // Validate inputs
  if (!color1 || typeof color1 !== 'string' || !color1.startsWith('#') || color1.length < 7) {
    console.error(`Invalid color1: ${color1}, returning color2`);
    return color2 || '#000000';
  }
  if (!color2 || typeof color2 !== 'string' || !color2.startsWith('#') || color2.length < 7) {
    console.error(`Invalid color2: ${color2}, returning color1`);
    return color1;
  }

  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);

  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);

  // Check for NaN values
  if (isNaN(r1) || isNaN(g1) || isNaN(b1)) {
    console.error(`Failed to parse color1: ${color1}, returning color2`);
    return color2;
  }
  if (isNaN(r2) || isNaN(g2) || isNaN(b2)) {
    console.error(`Failed to parse color2: ${color2}, returning color1`);
    return color1;
  }

  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Generate main page background with gradient support
 *
 * @param localTheme Transformed theme colors
 * @param customization User customization settings
 * @param fallbackBg Fallback background color
 * @returns CSS background string
 */
export const getBackground = (
  localTheme: ThemeColors,
  customization: CustomizationSettings,
  fallbackBg: string = '#0891b2'
): string => {
  // Ensure we have valid colors
  const primaryColor = localTheme.primaryColor || fallbackBg;
  const secondaryColor = localTheme.secondaryColor || fallbackBg;

  if (customization.gradientEnabled) {
    const angle = customization.gradientAngle || 135;
    const favorability = customization.gradientFavorability ?? 50;
    const strength = (customization.gradientStrength ?? 100) / 100;

    // Apply gradient strength by blending secondary color toward primary
    const blendedSecondary = strength === 1
      ? secondaryColor
      : blendColors(primaryColor, secondaryColor, strength);

    // Apply gradient favorability (color balance) as color stop position
    // favorability: 0 = more secondary, 50 = balanced, 100 = more primary
    const colorStop = favorability * 2; // Convert 0-100 to 0-200%

    return `linear-gradient(${angle}deg, ${primaryColor} 0%, ${blendedSecondary} ${colorStop}%)`;
  }
  return primaryColor;
};

/**
 * Generate card background with gradient and opacity support
 *
 * @param localTheme Transformed theme colors
 * @param customization User customization settings
 * @param fallbackBg Fallback background color
 * @returns CSS background string
 */
export const getCardBackground = (
  localTheme: ThemeColors,
  customization: CustomizationSettings,
  fallbackBg: string = '#0891b2'
): string => {
  const opacity = (customization.cardOpacity ?? 100) / 100;

  if (customization.cardGradientEnabled) {
    const fav = customization.cardGradientFavorability ?? 50;
    const strength = (customization.cardGradientStrength ?? 100) / 100;

    // Ensure we have valid colors with proper fallbacks
    const color1 = localTheme.cardGradientColor1 || localTheme.cardBg || fallbackBg;
    const color2 = localTheme.cardGradientColor2 || localTheme.cardBg || fallbackBg;

    // Only process if we have hex colors
    if (!color1.startsWith('#') || !color2.startsWith('#')) {
      // If not hex colors, fall back to solid color
      const baseColor = color1.startsWith('#') ? color1 : (color2.startsWith('#') ? color2 : fallbackBg);
      return hexToRgba(baseColor, opacity);
    }

    const blendedColor2 = strength === 1 ? color2 : blendColors(color1, color2, strength);

    const rgba1 = hexToRgba(color1, opacity);
    const rgba2 = hexToRgba(blendedColor2, opacity);

    return `linear-gradient(${customization.cardGradientAngle || 135}deg, ${rgba1} 0%, ${rgba2} ${fav * 2}%)`;
  } else {
    // Use cardGradientColor1 as primary card color
    const baseColor = localTheme.cardGradientColor1 || localTheme.cardBg || fallbackBg;
    if (baseColor.startsWith('#')) {
      return hexToRgba(baseColor, opacity);
    }
    // If it's already rgba, multiply opacity with original alpha
    if (baseColor.startsWith('rgba')) {
      const rgbaMatch = baseColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
      if (rgbaMatch) {
        const [, r, g, b, a] = rgbaMatch;
        const originalAlpha = a ? parseFloat(a) : 1.0;
        const finalAlpha = originalAlpha * opacity;
        return `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
      }
    }
    return baseColor;
  }
};

/**
 * Calculate navbar background from primary color with opacity
 *
 * @param primaryColor Transformed primary color (hex)
 * @param opacity Navbar opacity (0-100)
 * @param fallback Optional fallback color
 * @returns CSS rgba string
 */
export const calculateNavbarBg = (primaryColor: string, opacity: number = 92, fallback?: string): string => {
  const alpha = opacity / 100;

  // Use hexToRgba with fallback support
  return hexToRgba(primaryColor, alpha, fallback);
};
